<template>
    <div v-cloak class="app flex-row align-items-center">
        <div class="container">
            <b-row class="justify-content-center">
                <b-col md="6">
                    <b-card-group>
                        <b-card no-body class="p-0">
                            <b-card-header class="pl-0">
                                <span>{{$t('EASE_OF_BUSINESS')}}</span>
                                <h1>{{$t('LOGIN')}}</h1>
                            </b-card-header>

                            <b-card-body class="p-0">
                                <!-- Step 1: Username & Password Form -->
                                <b-form @submit.prevent="submitLogin" v-show="!mfaRequired" autocomplete="off">
                                    <b-input-group class="mb-3">
                                        <b-input-group-prepend>
                                            <b-input-group-text><i>@</i></b-input-group-text>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            :state="usernameState"
                                            type="text"
                                            class="form-control"
                                            :placeholder="$t('EMAIL')"
                                            aria-describedby="inputUsernameFeedback"
                                            v-model.trim="username">
                                        </b-form-input>
                                        <b-form-invalid-feedback id="inputUsernameFeedback">
                                            {{$t('USERNAME_MANDATORY')}}
                                        </b-form-invalid-feedback>
                                    </b-input-group>
                                    <b-input-group class="mb-1">
                                        <b-input-group-prepend>
                                            <b-input-group-text><i class="icon-lock"></i></b-input-group-text>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            type="password"
                                            :state="passwordState"
                                            class="form-control"
                                            :placeholder="$t('PASSWORD')"
                                            aria-describedby="inputPasswordFeedback"
                                            v-model="password">
                                        </b-form-input>
                                        <b-form-invalid-feedback id="inputPasswordFeedback">
                                            {{$t('PASSWORD_MANDATORY')}}
                                        </b-form-invalid-feedback>
                                    </b-input-group>
                                    <b-row class="mb-3">
                                        <b-col class="text-left">
                                            <a
                                                :href="redirectToForgetPassword" target='_top'>{{$t("FORGOT_PASSWORD")}}
                                            </a>
                                        </b-col>
                                    </b-row>
                                    <b-row class="mb-3">
                                        <b-col cols="6">
                                            <app-submit :disabled="maintenance" button-class="button-min-width" :loading="show">{{$t('LOGIN')}}</app-submit>
                                        </b-col>
                                        <b-col cols="6">
                                            <p class="text-right">
                                                <a :href="redirectToRegister" target='_top' class="ml-2">{{$t('NOT_REGISTER_YET')}}</a>
                                            </p>
                                        </b-col>
                                    </b-row>
                                    <b-row class="mt-4">
                                        <b-col>
                                            <b-alert variant="danger" :show="maintenance">
                                               {{$t('MAINTENANCE_MESSAGE')}}
                                             </b-alert>
                                        </b-col>
                                    </b-row>
                                    <b-row class="mt-4">
                                        <b-col>
                                            <b-alert variant="danger" :show="showError">{{errorMessage}}
                                            </b-alert>
                                        </b-col>
                                    </b-row>
                                </b-form>
                                <!-- Step 2: MFA Verification Form -->
                                <b-form @submit.prevent="submitMfaCode" v-show="mfaRequired" autocomplete="off">
                                    <div class="mb-3 d-flex justify-content-between align-items-center">
                                        <h5 class="pl-0 mb-0">{{$t('TWO_FACTOR_AUTHENTICATION')}}</h5>
                                        <!-- Session Expiry Timer -->
                                        <div v-if="sessionExpiry && sessionExpiry < this.SESSION_TIMEOUT" class="d-flex align-items-center px-2 py-1 rounded text-white" style="background-color:#fb6b41;">
                                            <i class="icon-clock mr-1"></i>
                                            <span>{{ Math.floor(sessionExpiry / 60) }}:{{ String(sessionExpiry % 60).padStart(2, '0') }}</span>
                                        </div>
                                    </div>
                                    <p class="text-muted">{{$t('MFA_INSTRUCTION')}}</p>

                                    <!-- MFA Success Alert -->
                                    <b-alert variant="success" show class="mb-3">
                                        <i class="icon-check mr-2"></i>
                                        {{$t('MFA_CODE_SENT_TO')}}
                                        <strong>{{maskedEmail}}</strong>
                                    </b-alert>

                                    <!-- Code Input -->
                                    <b-input-group class="mb-3">
                                        <b-input-group-prepend>
                                            <b-input-group-text><i class="icon-key"></i></b-input-group-text>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            type="text"
                                            :state="mfaCodeState"
                                            class="form-control"
                                            :placeholder="$t('ENTER_VERIFICATION_CODE')"
                                            aria-describedby="inputMfaCodeFeedback"
                                            v-model="mfaCode"
                                            maxlength="6"
                                            @input="clearMfaError">
                                        </b-form-input>
                                        <b-form-invalid-feedback id="inputMfaCodeFeedback">
                                            {{$t('MFA_CODE_MANDATORY')}}
                                        </b-form-invalid-feedback>
                                    </b-input-group>

                                    <b-row class="mb-3">
                                        <b-col cols="6">
                                            <app-submit
                                                :disabled="maintenance || !mfaCode || verifyingMfa"
                                                button-class="button-min-width"
                                                :loading="verifyingMfa">
                                                {{$t('VERIFY_CODE')}}
                                            </app-submit>
                                        </b-col>
                                        <b-col cols="6" class="d-flex flex-column align-items-end gap-0">
                                            <b-button
                                                variant="link"
                                                @click="resendMfaCode"
                                                :disabled="resendingCode || resendCooldown > 0"
                                                class="p-0 mb-2 action_button">
                                                <b-spinner small v-if="resendingCode"></b-spinner>
                                                {{resendCooldown > 0 ? $t('RESEND_IN') + ' ' + resendCooldown + 's' : $t('RESEND_CODE')}}
                                            </b-button>
                                            <b-button
                                                variant="link"
                                                @click="backToLogin"
                                                class="p-0 mt-0 action_button">
                                                {{$t('BACK_TO_LOGIN')}}
                                            </b-button>
                                        </b-col>
                                    </b-row>

                                    <!-- MFA Error Messages -->
                                    <b-row class="mt-1" style="line-height: 0.6 !important;">
                                        <b-col>
                                            <div v-if="showMfaError" class="mt-n2" style="color: red; font-weight:bold;">
                                                {{ mfaErrorMessage }}
                                            </div>
                                        </b-col>
                                    </b-row>
                                </b-form>
                            </b-card-body>
                        </b-card>

                    </b-card-group>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
    import routeName from '@/router/routeNames'
    import AppValidationDescription from "@/components/app/form/AppValidationDescription";
    import {getErrorMessage} from '@/mixins/error/getErrorMessage'
    import {
        RegisterPageEn,
        RegisterPageHr,
        RegisterPageEs, RegisterPageIt, RegisterPageNl, RegisterPageDe, RegisterPageDa,
        ForgotPasswordPageEn,
        ForgotPasswordPageHr, ForgotPasswordPageEs, ForgotPasswordPageDa, ForgotPasswordPageIt, ForgotPasswordPageNl,
        LS_PRESETS, ForgotPasswordPageFallback, RegisterPageFallback, ForgotPasswordPageDe,
    } from "@/shared/constants";
    import AppButton from "@/components/app/AppButton/AppButton";
    import AppSubmit from "@/components/app/AppButton/AppButtonSubmit";
    import {getPresets} from "@/services/user";
    export default {
        name: 'Login',
        mixins: [getErrorMessage],
        components: {
            AppSubmit,
            AppButton,
            AppValidationDescription,
        },
        data() {
            return {
                maintenance:false,
                routeName,
                username: '',
                password: '',
                usernameState: null,
                passwordState: null,
                show: false,
                label: 'Logging in...',
                overlay: true,
                error: "",
                showError: false,
                showSuccessMessage: false,

                // MFA data
                SESSION_TIMEOUT: 600,
                RESEND_COOLDOWN_SECONDS: 60,
                mfaRequired: false,
                mfaSessionToken: null,
                mfaCode: '',
                mfaCodeState: null,
                maskedEmail: '',
                sessionExpiry: null,
                resendCooldown: 0,
                verifyingMfa: false,
                resendingCode: false,
                mfaError: "",
                showMfaError: false,

                // Timers
                sessionTimer: null,
                resendTimer: null,
            }
        },
        computed: {
            errorMessage: function () {
                return this.error
            },
            mfaErrorMessage: function () {
                return this.mfaError
            },
            redirectToForgetPassword() {
                if (this.$i18n.locale == 'hr') {
                    return ForgotPasswordPageHr
                }
                if (this.$i18n.locale == 'en'){
                    return ForgotPasswordPageEn
                }
                if (this.$i18n.locale == 'es'){
                    return ForgotPasswordPageEs
                }
                if (this.$i18n.locale == 'da'){
                    return ForgotPasswordPageDa
                }
                if (this.$i18n.locale == 'de'){
                    return ForgotPasswordPageDe
                }
                if (this.$i18n.locale == 'it'){
                    return ForgotPasswordPageIt
                }
                if (this.$i18n.locale == 'nl'){
                    return ForgotPasswordPageNl
                }
                return ForgotPasswordPageFallback + this.$i18n.locale
            },
            redirectToRegister() {
                if (this.$i18n.locale == 'hr') {
                    return RegisterPageHr
                }
                if (this.$i18n.locale == 'en'){
                    return RegisterPageEn
                }
                if (this.$i18n.locale == 'es'){
                    return RegisterPageEs
                }
                if (this.$i18n.locale == 'da'){
                    return RegisterPageDa
                }
                if (this.$i18n.locale == 'de'){
                    return RegisterPageDe
                }
                if (this.$i18n.locale == 'it'){
                    return RegisterPageIt
                }
                if (this.$i18n.locale == 'nl'){
                    return RegisterPageNl
                }
                return RegisterPageFallback + this.$i18n.locale
            }

        },

        methods: {
            submitLogin() {
                /**
                 * Submit initial login (username & password)
                 */
                localStorage.clear();
                const formData = {
                    password: this.password,
                    username: this.username
                }
                this.show = true
                this.showError = false
                this.showSuccessMessage = false

                this.clearMfaError()

                this.$store.dispatch('auth/actionLogin', {
                    username: formData.username,
                    password: formData.password,
                    lang: this.$i18n.locale
                })
                    .then(response => {
                        if (response && response.mfa_session_token) {
                            this.handleMfaRequired(response)
                        } else if (response && response.access_token) {
                            this.completeLogin()
                        }
                    })
                    .catch(error => {
                        this.handleLoginError(error)
                    })
                    .finally(() => {
                        this.show = false
                    })
            },

            /**
             * Handle MFA required response
             */
            handleMfaRequired(response) {
                this.mfaRequired = true
                this.mfaSessionToken = response.mfa_session_token
                this.maskedEmail = response.masked_email
                this.sessionExpiry = response.expires_in

                // Start session expiry countdown
                this.startSessionTimer()

                // Start cooldown for resend button
                this.startResendCooldown(this.RESEND_COOLDOWN_SECONDS)
            },

            /**
             * Submit MFA verification code
             */
            submitMfaCode() {
                if (!this.mfaCode || this.mfaCode.length !== 6) {
                    this.mfaCodeState = false
                    return
                }

                this.verifyingMfa = true
                this.clearMfaError()
                this.mfaCodeState = null

                this.$store.dispatch('auth/actionVerifyMfa', {
                    mfa_session_token: this.mfaSessionToken,
                    otp_code: this.mfaCode,
                    lang: this.$i18n.locale
                })
                    .then(() => {
                        // MFA verification successful, complete login
                        this.completeLogin()
                    })
                    .catch(error => {
                        this.handleMfaError(error)
                        this.mfaCodeState = false
                    })
                    .finally(() => {
                        this.verifyingMfa = false
                    })
            },

            /**
             * Resend MFA code
             */
            resendMfaCode() {
                if (this.resendingCode || this.resendCooldown > 0) {
                    return
                }

                this.resendingCode = true
                this.clearMfaError()

                this.$store.dispatch('auth/actionResendMfaOtp', {
                    mfa_session_token: this.mfaSessionToken,
                    lang: this.$i18n.locale
                })
                    .then(response => {
                        this.startResendCooldown(this.RESEND_COOLDOWN_SECONDS);
                    })
                    .catch(error => {
                        this.handleMfaError(error)
                    })
                    .finally(() => {
                        this.resendingCode = false
                    })
            },

            /**
             * Go back to login form
             */
            backToLogin() {
                // Cancel MFA session
                if (this.mfaSessionToken) {
                    this.$store.dispatch('auth/actionCancelMfa', {
                        mfa_session_token: this.mfaSessionToken,
                        lang: this.$i18n.locale
                    }).catch(error => {
                        this.handleMfaError(error)
                    })
                }

                this.resetMfaState()
                this.resetLoginForm()
            },

            /**
             * Complete login process
             */
            completeLogin() {
                let link = this.$router.currentRoute.query.redirect ? this.$router.currentRoute.query.redirect : '/'

                this.$store.dispatch('user/actionSetUser').then(() => {
                    this.$store.dispatch('user/actionSetUserPermissions').then(() => {
                        if (!this.$router.currentRoute.query.redirect) {
                            this.$router.replace({name: routeName.RN_HOME})
                        } else {
                            if (this.$router.currentRoute.query.redirect === '/login') {
                                this.$router.replace({name: routeName.RN_HOME})
                            } else {
                                this.$router.push(link)
                            }
                        }
                    })
                })
            },

            /**
             * Handle login errors
             */
            handleLoginError(error) {
                if (error.response.status === 302 && error.response.data.user_id) {
                    this.$router.replace({
                        name: routeName.RN_CHANGE_EXPIRED_PASSWORD,
                        query: {
                            user_id: error.response.data.user_id,
                            lang: this.$i18n.locale
                        }
                    })
                }
                this.password = null
                this.error = error.response.data.message_description || 'Login failed'
                this.showError = true
            },

            /**
             * Handle MFA errors
             */
            handleMfaError(error) {
                this.mfaError = error.response.data.message_description || 'MFA verification failed'
                this.showMfaError = true

                this.mfaCode = ''
            },

            /**
             * Clear MFA error messages
             */
            clearMfaError() {
                this.showMfaError = false
                this.mfaError = ""
                this.mfaCodeState = null
            },

            /**
             * Start session expiry timer
             */
            startSessionTimer() {
                if (this.sessionTimer) {
                    clearInterval(this.sessionTimer)
                }

                this.sessionTimer = setInterval(() => {
                    if (this.sessionExpiry > 0) {
                        this.sessionExpiry--
                    } else {
                        this.handleSessionExpiry()
                    }
                }, 1000)
            },

            /**
             * Start resend cooldown timer
             */
            startResendCooldown(seconds) {
                this.resendCooldown = seconds

                if (this.resendTimer) {
                    clearInterval(this.resendTimer)
                }

                this.resendTimer = setInterval(() => {
                    if (this.resendCooldown > 0) {
                        this.resendCooldown--
                    } else {
                        clearInterval(this.resendTimer)
                    }
                }, 1000)
            },

            /**
             * Handle session expiry
             */
            handleSessionExpiry() {
                this.mfaError = this.$t('SESSION_EXPIRED')
                this.showMfaError = true
                this.backToLogin()
            },

            /**
             * Reset MFA state
             */
            resetMfaState() {
                this.mfaRequired = false
                this.mfaSessionToken = null
                this.mfaCode = ''
                this.mfaCodeState = null
                this.maskedEmail = ''
                this.sessionExpiry = null
                this.resendCooldown = 0
                this.verifyingMfa = false
                this.resendingCode = false
                this.clearMfaError()

                // Clear timers
                if (this.sessionTimer) {
                    clearInterval(this.sessionTimer)
                    this.sessionTimer = null
                }
                if (this.resendTimer) {
                    clearInterval(this.resendTimer)
                    this.resendTimer = null
                }
            },

            /**
             * Reset login form
             */
            resetLoginForm() {
                this.username = ''
                this.password = ''
                this.usernameState = null
                this.passwordState = null
                this.showError = false
                this.error = ''
            }
        },
        watch:{
            '$route.query.lang':{
                handler(value){
                    if(value === 'sl'){
                        this.$i18n.locale = 'si'
                    }  else  {
                        this.$i18n.locale = value ? value  : 'en'
                    }
                },
                immediate:true
            }
        },
        beforeRouteEnter(to, from, next) {
            document.body.classList.add('page-login');
            document.body.classList.remove('page-w-login')
            next()
        },

        beforeDestroy() {
            // Clean up timers
            if (this.sessionTimer) {
                clearInterval(this.sessionTimer)
            }
            if (this.resendTimer) {
                clearInterval(this.resendTimer)
            }
        }
    }

</script>
